import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, X, Flag, Send, Loader2, Camera, Check, Upload, Sparkles, Settings, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { io } from 'socket.io-client';
import api from '../utils/api';
import { isAuthenticated, removeToken, getToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = '/api/socket.io';

export default function Dashboard() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [matchesRemaining, setMatchesRemaining] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showUnmatchDialog, setShowUnmatchDialog] = useState(false);
  const [showPhotoVerify, setShowPhotoVerify] = useState(false);
  const [unmatchingId, setUnmatchingId] = useState(null);
  const socketRef = useRef(null);

  const loadData = async () => {
    try {
      const profileRes = await api.get('/user/profile');
      setProfile(profileRes.data);

      const matchesRes = await api.get('/matches/my-matches');
      setMatches(matchesRes.data.matches);
      setMatchesRemaining(matchesRes.data.matches_remaining);

      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    loadData();
    
    // Send heartbeat every 2 minutes to mark user as active
    const heartbeatInterval = setInterval(() => {
      api.post('/user/heartbeat').catch(() => {});
    }, 2 * 60 * 1000); // 2 minutes
    
    // Send initial heartbeat
    api.post('/user/heartbeat').catch(() => {});
    
    return () => clearInterval(heartbeatInterval);
  }, [navigate]);

  // Auto-retry matching if user has remaining matches but no current matches
  useEffect(() => {
    if (!loading && matches.length === 0 && matchesRemaining > 0) {
      handleRetryMatching();
    }
  }, [loading, matches.length, matchesRemaining]);

  const handleRetryMatching = async () => {
    if (retrying || matchesRemaining <= 0) return;
    
    setRetrying(true);
    try {
      const res = await api.post('/matches/retry');
      
      if (res.data.success && res.data.matches_generated > 0) {
        toast.success(res.data.message);
        // Reload data to get new matches
        await loadData();
      } else {
        // Don't show error toast on auto-retry, only on manual retry
        if (!res.data.success) {
          setMatchesRemaining(res.data.matches_remaining);
        }
      }
    } catch (error) {
      console.error('Retry matching error:', error);
    } finally {
      setRetrying(false);
    }
  };

  const handleManualRetry = async () => {
    if (retrying) return;
    
    setRetrying(true);
    try {
      const res = await api.post('/matches/retry');
      
      if (res.data.success && res.data.matches_generated > 0) {
        toast.success(res.data.message);
        await loadData();
      } else if (res.data.matches_remaining === 0) {
        // No matches remaining - redirect to payment
        toast.info('No matches remaining. Get more matches to continue!');
        setMatchesRemaining(0);
      } else {
        // Has remaining matches but no one available
        toast.info('No compatible users found yet. We\'ll notify you when matches are available!');
        setMatchesRemaining(res.data.matches_remaining);
      }
    } catch (error) {
      // Friendly error message
      toast.info('No compatible matches found yet. We\'ll keep looking and notify you!');
    } finally {
      setRetrying(false);
    }
  };

  const handlePhotoVerification = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/user/upload-verification-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Photo verified!');
      setProfile({ ...profile, is_photo_verified: true });
      setShowPhotoVerify(false);
      
      // Refresh data to get updated matches if user got free matches
      if (response.data.gets_free_matches) {
        loadData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Photo verification failed. Please take a clear selfie.';
      toast.error(errorMsg);
    }
  };

  const handleGetNewMatches = () => {
    navigate('/paywall');
  };

  const handleOpenChat = (match) => {
    setSelectedMatch(match);
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setSelectedMatch(null);
  };

  const handleUnmatchClick = (matchId) => {
    setUnmatchingId(matchId);
    setShowUnmatchDialog(true);
  };

  const handleConfirmUnmatch = async () => {
    if (!unmatchingId) return;

    try {
      await api.delete(`/chat/${unmatchingId}/unmatch`);
      setMatches(matches.filter(m => m.match_id !== unmatchingId));
      setShowUnmatchDialog(false);
      setUnmatchingId(null);
      toast.success('Unmatched successfully');
      
      if (selectedMatch?.match_id === unmatchingId) {
        handleCloseChat();
      }
    } catch (error) {
      toast.error('Failed to unmatch');
    }
  };

  const handleLogout = () => {
    removeToken();
    navigate('/');
  };

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await api.delete('/user/delete-account');
      toast.success('Your account has been permanently deleted.');
      removeToken();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account. Please try again.');
    } finally {
      setDeletingAccount(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="app-container gradient-bg min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  return (
    <div className="app-container gradient-bg">
      {/* Top Bar */}
      <div className="bg-primary h-16 flex items-center justify-between px-4 sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Heart className="text-white fill-white" size={24} />
          <span className="text-white font-bold text-xl">BlindMatch</span>
        </div>
        <div className="flex items-center gap-4">
          {!profile?.is_photo_verified && (
            <button
              onClick={() => setShowPhotoVerify(true)}
              className="flex items-center gap-1 bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-amber-600"
              data-testid="verify-photo-button"
            >
              <Camera size={14} />
              Verify
            </button>
          )}
          {profile?.is_photo_verified && (
            <div className="flex items-center gap-1 bg-green-500/20 border border-green-500 text-green-400 text-xs font-semibold px-3 py-1.5 rounded-full">
              <Check size={14} />
              Verified
            </div>
          )}
          <span className="text-white text-sm">{profile?.city}</span>
          <button onClick={handleLogout} className="text-white text-sm hover:underline">
            Logout
          </button>
          <button 
            onClick={() => setShowDeleteDialog(true)} 
            className="text-red-400 text-sm hover:underline"
            data-testid="delete-account-btn"
          >
            Delete Account
          </button>
        </div>
      </div>

      <div className="px-4 py-6 pb-24">
        {/* Email not verified yet (email/password signups only - Google is pre-verified) */}
        {profile && !profile.is_verified && profile.auth_provider !== 'google' && (
          <div className="glass rounded-xl p-4 mb-6 border border-amber-500/30" data-testid="email-verify-banner">
            <p className="text-gray-300 text-sm mb-3">
              Your email <span className="text-white font-medium">{profile.email}</span> isn't verified yet.
              You can keep using BlindMatch — verify whenever you're ready.
            </p>
            <button
              onClick={() => navigate('/verify-email', { state: { email: profile.email, returnTo: '/dashboard' } })}
              className="h-10 px-4 bg-amber-500 text-white text-sm font-semibold rounded-full hover:bg-amber-600 transition-colors"
              data-testid="email-verify-banner-button"
            >
              Verify email
            </button>
          </div>
        )}

        {/* Expired Banner */}
        {matchesRemaining === 0 && matches.length === 0 && (
          <div className="glass rounded-xl p-4 mb-6 border border-accent/30" data-testid="expired-banner">
            <p className="text-amber-400 text-center mb-3 font-semibold flex items-center justify-center gap-2">
              <Sparkles size={20} />
              Get more matches today!
            </p>
            <button
              onClick={handleGetNewMatches}
              className="w-full h-12 bg-accent text-background font-semibold rounded-full btn-glow transition-all duration-300 active:scale-95"
              data-testid="get-new-matches-button"
            >
              Get New Matches →
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-white font-semibold text-2xl mb-1">Your Anonymous Matches</h2>
          <p className="text-gray-400 text-sm">
            Chat freely. Reveal only when you're comfortable.
          </p>
        </div>

        {/* Matches List */}
        {matches.length === 0 ? (
          <div className="text-center py-12" data-testid="empty-matches">
            <div className="glass rounded-3xl p-8 border border-primary/20">
              <div className="text-6xl mb-4">{retrying ? '⏳' : '🔍'}</div>
              <h3 className="text-white font-bold text-xl mb-2">
                {retrying ? 'Finding your matches...' : 'Looking for your matches...'}
              </h3>
              {matchesRemaining > 0 && matchesRemaining < 100 ? (
                <>
                  <p className="text-gray-400 text-sm mb-4">
                    {retrying 
                      ? 'Please wait while we search for compatible people...'
                      : 'We\'re actively searching for compatible matches based on your preferences.'
                    }
                  </p>
                  
                  {/* Retry Button */}
                  <button
                    onClick={handleManualRetry}
                    disabled={retrying}
                    className="px-6 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 mb-4 flex items-center gap-2 mx-auto disabled:opacity-50"
                    data-testid="retry-matching-btn"
                  >
                    {retrying ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Searching...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={18} />
                        Find Matches Now
                      </>
                    )}
                  </button>

                  <div className="glass rounded-xl p-4 border border-green-500/30 mb-4">
                    <p className="text-green-400 text-sm">
                      No compatible users found yet. We'll notify you via email when matches become available!
                    </p>
                  </div>
                  
                  <p className="text-gray-500 text-xs mb-4">
                    Matches remaining: {matchesRemaining}
                  </p>
                  
                  <button
                    onClick={() => navigate('/edit-profile')}
                    className="px-6 h-10 bg-secondary/50 text-white font-medium rounded-full border border-white/20 hover:border-primary/50 transition-all duration-300 flex items-center gap-2 mx-auto"
                    data-testid="edit-profile-btn"
                  >
                    <Settings size={16} />
                    Edit Preferences
                  </button>
                </>
              ) : profile?.gets_free_matches ? (
                <>
                  <p className="text-gray-400 text-sm mb-4">
                    No matches available right now. We'll notify you when someone compatible joins!
                  </p>
                  <button
                    onClick={handleManualRetry}
                    disabled={retrying}
                    className="px-6 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 mb-4 flex items-center gap-2 mx-auto disabled:opacity-50"
                    data-testid="retry-matching-btn-free"
                  >
                    {retrying ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Searching...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={18} />
                        Check for Matches
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => navigate('/edit-profile')}
                    className="px-6 h-10 bg-secondary/50 text-white font-medium rounded-full border border-white/20 hover:border-primary/50 transition-all duration-300 flex items-center gap-2 mx-auto"
                    data-testid="edit-profile-btn"
                  >
                    <Settings size={16} />
                    Edit Preferences
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-400 text-sm mb-4">
                    Your matches have been exhausted. Get new matches to continue!
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleGetNewMatches}
                      className="px-6 h-12 bg-accent text-background font-semibold rounded-full btn-glow transition-all duration-300 active:scale-95"
                      data-testid="get-matches-cta"
                    >
                      Get 3 Matches →
                    </button>
                    <button
                      onClick={() => navigate('/edit-profile')}
                      className="px-6 h-10 bg-transparent text-gray-400 font-medium rounded-full border border-white/20 hover:border-primary/50 hover:text-white transition-all duration-300 flex items-center gap-2 justify-center"
                      data-testid="edit-profile-btn-secondary"
                    >
                      <Settings size={16} />
                      Edit Profile & Preferences
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {matches.map((match) => (
              <div
                key={match.match_id}
                className="card-enhanced rounded-2xl p-4"
                data-testid={`match-card-${match.match_id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0 relative">
                    <Heart className="text-white fill-white" size={24} />
                    {match.is_photo_verified && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-background">
                        <Check size={10} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-semibold">Blind Match #{match.match_id}</h3>
                        {match.is_photo_verified && (
                          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full font-medium">Verified</span>
                        )}
                      </div>
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                    </div>
                    <div className="flex flex-wrap gap-2 text-gray-400 text-sm">
                      <span>🎂 {match.age_range}</span>
                      <span>📍 {match.city}</span>
                      {match.relationship_type && (
                        <span>💕 {match.relationship_type.replace('-', ' ')}</span>
                      )}
                    </div>
                    {/* Bio display */}
                    {match.bio && (
                      <p className="text-gray-300 text-sm mt-2 line-clamp-2 italic">
                        "{match.bio}"
                      </p>
                    )}
                    <div className="mt-2">
                      <span className="text-gray-400 text-xs">🎯 Shared: </span>
                      <span className="text-gray-400 text-xs">
                        {match.shared_interests.length > 0 
                          ? match.shared_interests.slice(0, 5).join(', ')
                          : 'Finding common ground...'}
                        {match.shared_interests.length > 5 && ` +${match.shared_interests.length - 5} more`}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenChat(match)}
                    className="flex-1 h-10 bg-primary hover:bg-primary/90 text-white font-medium rounded-full transition-all duration-300 active:scale-95 flex items-center justify-center gap-2"
                    data-testid={`open-chat-${match.match_id}`}
                  >
                    <MessageCircle size={16} />
                    Open Chat
                  </button>
                  <button
                    onClick={() => handleUnmatchClick(match.match_id)}
                    className="h-10 px-4 border-2 border-red-500 text-red-500 font-medium rounded-full hover:bg-red-500/10 transition-all duration-300 active:scale-95 flex items-center justify-center"
                    data-testid={`unmatch-${match.match_id}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat Modal */}
      {showChat && selectedMatch && (
        <ChatModal
          match={selectedMatch}
          onClose={handleCloseChat}
          socketRef={socketRef}
          userEmail={profile?.email}
        />
      )}

      {/* Unmatch Dialog */}
      {showUnmatchDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4" data-testid="unmatch-dialog">
          <div className="glass rounded-2xl p-6 max-w-sm w-full border border-primary/30">
            <h3 className="text-white font-bold text-xl mb-2">Unmatch?</h3>
            <p className="text-gray-400 text-sm mb-6">
              This will end your chat permanently. Cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowUnmatchDialog(false);
                  setUnmatchingId(null);
                }}
                className="flex-1 h-12 border-2 border-gray-500 text-gray-300 font-semibold rounded-full hover:bg-gray-500/10 transition-all"
                data-testid="unmatch-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUnmatch}
                className="flex-1 h-12 bg-red-500 text-white font-semibold rounded-full hover:bg-red-600 transition-all"
                data-testid="unmatch-confirm"
              >
                Yes, Unmatch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 px-4" data-testid="delete-account-dialog">
          <div className="glass rounded-2xl p-6 max-w-sm w-full border border-red-500/50">
            <h3 className="text-white font-bold text-xl mb-2">Delete Account?</h3>
            <p className="text-gray-400 text-sm mb-4">
              This will permanently delete your account and all associated data including:
            </p>
            <ul className="text-gray-400 text-sm mb-4 space-y-1">
              <li>• Your profile information</li>
              <li>• All your matches and chat history</li>
              <li>• Any unused match credits</li>
            </ul>
            <p className="text-red-400 text-sm font-semibold mb-6">
              This action cannot be undone. You will need to create a new account and purchase matches again.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={deletingAccount}
                className="flex-1 h-12 border-2 border-gray-500 text-gray-300 font-semibold rounded-full hover:bg-gray-500/10 transition-all disabled:opacity-50"
                data-testid="delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="flex-1 h-12 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="delete-confirm"
              >
                {deletingAccount ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  'Delete Forever'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Verification Dialog */}
      {showPhotoVerify && (
        <PhotoVerificationModal
          onClose={() => setShowPhotoVerify(false)}
          onVerify={handlePhotoVerification}
        />
      )}
    </div>
  );
}

// Photo Verification Modal - Live Camera Capture
function PhotoVerificationModal({ onClose, onVerify }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    // Request camera permission on mount
    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: 640, height: 480 }
        });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.onloadedmetadata = () => {
            setCameraReady(true);
          };
        }
      } catch (err) {
        console.error('Camera access error:', err);
        setPermissionDenied(true);
        toast.error('Camera access required for verification');
      }
    };
    
    startCamera();
    
    // Cleanup on unmount
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setCapturing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(imageDataUrl);
    setCapturing(false);
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  const handleSubmit = async () => {
    if (!capturedImage) return;
    
    setUploading(true);
    try {
      // Convert data URL to Blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      
      await onVerify(file);
      
      // Stop camera
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    // Stop camera before closing
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 px-4">
      <div className="glass rounded-3xl p-6 max-w-md w-full border border-primary/30">
        <div className="text-center mb-4">
          <h3 className="text-white font-bold text-2xl mb-2">Live Verification</h3>
          <p className="text-gray-400 text-sm">
            Take a real-time selfie to get your verified badge
          </p>
        </div>

        {permissionDenied ? (
          <div className="text-center py-8">
            <Camera className="text-red-500 mx-auto mb-4" size={48} />
            <p className="text-red-400 mb-4">Camera access denied</p>
            <p className="text-gray-400 text-sm mb-4">
              Please allow camera access in your browser settings to verify your profile.
            </p>
            <button
              onClick={handleClose}
              className="w-full h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Camera View / Captured Image */}
            <div className="relative rounded-2xl overflow-hidden mb-4 bg-black aspect-video">
              {capturedImage ? (
                <img 
                  src={capturedImage} 
                  alt="Captured selfie" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted
                    className="w-full h-full object-cover mirror"
                  />
                  {!cameraReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="animate-spin text-primary" size={32} />
                    </div>
                  )}
                  {/* Face guide overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-60 border-2 border-dashed border-primary/50 rounded-full" />
                  </div>
                </>
              )}
            </div>
            
            {/* Hidden canvas for capturing */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Instructions */}
            <div className="glass rounded-xl p-3 mb-4 border border-primary/20">
              <p className="text-gray-300 text-xs text-center">
                Position your face within the oval guide and ensure good lighting
              </p>
            </div>

            {/* Action Buttons */}
            {capturedImage ? (
              <div className="space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={uploading}
                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="verify-submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      Submit for Verification
                    </>
                  )}
                </button>
                <button
                  onClick={handleRetake}
                  disabled={uploading}
                  className="w-full h-10 bg-secondary/50 text-white font-medium rounded-full border border-white/10 disabled:opacity-50"
                >
                  Retake Photo
                </button>
              </div>
            ) : (
              <button
                onClick={handleCapture}
                disabled={!cameraReady || capturing}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="capture-selfie-btn"
              >
                {capturing ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <Camera size={20} />
                    Capture Selfie
                  </>
                )}
              </button>
            )}

            <button
              onClick={handleClose}
              className="w-full h-10 text-gray-400 hover:text-white transition-colors mt-3"
            >
              Maybe Later
            </button>
          </>
        )}
      </div>

      <style>{`
        .mirror {
          transform: scaleX(-1);
        }
      `}</style>
    </div>
  );
}

// Chat Modal Component - Real-time Chat
function ChatModal({ match, onClose, socketRef, userEmail }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initialize socket connection (capture instance so cleanup always tears it down)
    const socket = io(BACKEND_URL, { path: SOCKET_PATH, auth: { token: getToken() } });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected, joining room:', match.match_id);
      socketRef.current.emit('join_room', { room_id: match.match_id });
    });

    socketRef.current.on('new_message', (data) => {
      console.log('Received new message:', data);
      // Only add message if it's from the other user (not from ourselves)
      // Our messages are already added optimistically in handleSend
      if (data.chatroom === match.match_id && data.sender !== userEmail) {
        const newMsg = {
          text: data.text,
          timestamp: data.timestamp,
          is_mine: false,
          msg_id: data.msg_id,
          delivered: true,
          read: false
        };
        setMessages(prev => [...prev, newMsg]);
        
        // Mark as delivered
        if (data.msg_id) {
          socketRef.current.emit('message_delivered', {
            msg_id: data.msg_id,
            chatroom: match.match_id
          });
        }
        
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('New Message from Blind Match', {
            body: data.text,
            icon: '/heart-192.png'
          });
        }
      }
    });

    // Handle delivery receipts
    socketRef.current.on('delivery_receipt', (data) => {
      if (data.chatroom === match.match_id) {
        setMessages(prev => prev.map(msg => 
          msg.msg_id === data.msg_id ? { ...msg, delivered: true } : msg
        ));
      }
    });

    // Handle read receipts
    socketRef.current.on('read_receipt', (data) => {
      if (data.chatroom === match.match_id) {
        setMessages(prev => prev.map(msg => 
          msg.msg_id === data.msg_id ? { ...msg, read: true, delivered: true } : msg
        ));
      }
    });

    // Handle batch read notifications
    socketRef.current.on('messages_read', (data) => {
      if (data.chatroom === match.match_id) {
        setMessages(prev => prev.map(msg => 
          msg.is_mine ? { ...msg, read: true, delivered: true } : msg
        ));
      }
    });

    const loadMessages = async () => {
      try {
        const response = await api.get(`/chat/${match.match_id}/messages`);
        setMessages(response.data.messages);
      } catch (error) {
        console.error('Load messages error:', error);
        toast.error('Failed to load messages');
      } finally {
        setLoading(false);
      }
    };

    loadMessages();

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [match.match_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim() || sending) return;

    setSending(true);
    const textToSend = inputText;
    setInputText('');

    // Optimistic update
    const tempMsg = {
      text: textToSend,
      timestamp: new Date().toISOString(),
      is_mine: true,
      delivered: false,
      read: false,
      msg_id: 'temp-' + Date.now()
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const response = await api.post(`/chat/${match.match_id}/send`, { text: textToSend });
      // Update temp message with real msg_id
      setMessages(prev => prev.map(msg => 
        msg.msg_id === tempMsg.msg_id 
          ? { ...msg, msg_id: response.data.msg_id, delivered: true }
          : msg
      ));
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(msg => msg.msg_id !== tempMsg.msg_id));
      setInputText(textToSend);
    } finally {
      setSending(false);
    }
  };

  const handleReport = async (reason, details) => {
    try {
      await api.post('/report/submit', {
        chatroom_id: match.match_id,
        reason,
        details
      });
      toast.success('Report submitted. We review within 24 hours.');
      setShowReport(false);
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col" data-testid="chat-modal">
      {/* Header */}
      <div className="glass border-b border-white/10 p-4 flex items-center justify-between">
        <button onClick={onClose} className="text-white" data-testid="chat-close-button">
          <X size={24} />
        </button>
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-2">
            <h3 className="text-white font-semibold">Chat #{match.match_id}</h3>
            {match.is_photo_verified && (
              <span className="text-xs text-green-400">✓ Verified</span>
            )}
          </div>
          <p className="text-gray-400 text-xs">Anonymous Chat</p>
        </div>
        <button
          onClick={() => setShowReport(true)}
          className="text-gray-400 hover:text-white"
          data-testid="chat-report-button"
        >
          <Flag size={20} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <MessageCircle className="text-gray-600 mb-3" size={48} />
            <p className="text-gray-400">Start your anonymous conversation!</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={msg.msg_id || idx}
              className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}
            >
              <div className="max-w-[75%]">
                <div
                  className={`rounded-2xl px-4 py-2 ${
                    msg.is_mine
                      ? 'bg-primary text-white rounded-br-sm'
                      : 'bg-card text-white rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                </div>
                <div className="flex items-center gap-1 mt-1 px-2">
                  <p className="text-xs text-gray-500">
                    {msg.is_mine ? 'You' : 'Match'} · {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {/* Read receipt ticks for sent messages */}
                  {msg.is_mine && (
                    <span className={`text-xs ${msg.read ? 'text-blue-400' : msg.delivered ? 'text-gray-400' : 'text-gray-600'}`}>
                      {msg.read ? '✓✓' : msg.delivered ? '✓' : '○'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Safety Banner */}
      <div className="bg-accent/20 border-t border-accent/30 px-4 py-2">
        <p className="text-amber-400 text-xs text-center">
          🔒 Only share contact details when you truly feel comfortable
        </p>
      </div>

      {/* Input */}
      <div className="glass border-t border-white/10 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type a message..."
            className="flex-1 bg-secondary/50 border border-white/10 rounded-full h-12 px-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            data-testid="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim() || sending}
            className="w-12 h-12 rounded-full bg-primary text-white disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 active:scale-95"
            data-testid="chat-send-button"
          >
            <Send size={20} />
          </button>
        </div>
      </div>

      {/* Report Dialog */}
      {showReport && (
        <ReportDialog
          onClose={() => setShowReport(false)}
          onSubmit={handleReport}
        />
      )}
    </div>
  );
}

// Report Dialog Component
function ReportDialog({ onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');

  const handleSubmit = () => {
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }
    onSubmit(reason, details);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="report-dialog">
      <div className="glass rounded-2xl p-6 max-w-md w-full border border-primary/30">
        <h3 className="text-white font-bold text-xl mb-4">Report This User</h3>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary outline-none"
              data-testid="report-reason-select"
            >
              <option value="">Select reason</option>
              <option value="Harassment">Harassment</option>
              <option value="Inappropriate content">Inappropriate content</option>
              <option value="Fake profile">Fake profile</option>
              <option value="Spam">Spam</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Details (optional)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary outline-none resize-none"
              rows={3}
              placeholder="Provide additional details..."
              data-testid="report-details-textarea"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-12 border-2 border-gray-500 text-gray-300 font-semibold rounded-full hover:bg-gray-500/10 transition-all"
            data-testid="report-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 h-12 bg-red-500 text-white font-semibold rounded-full hover:bg-red-600 transition-all"
            data-testid="report-submit"
          >
            Submit Report
          </button>
        </div>
      </div>
    </div>
  );
}
